/* eslint-disable @typescript-eslint/no-shadow */
import React, { useCallback, useEffect, useState } from 'react'
import ReactDOM from 'react-dom'
import { connect } from 'react-redux'

import {
  COMMON_MIXPANEL_PROPERTIES,
  INACTIVITY_EVENTS,
  INACTIVITY_PROPERTIES,
  INACTIVITY_WARNING_DURATION_MS,
} from '@lyrahealth-inc/shared-app-logic'
import { INACTIVITY_STATE, InactivityWarningModal } from '@lyrahealth-inc/ui-core-crossplatform'

import { setTimeInactivityWarning, setTimeLastActive } from '../../../data/auth/authActions'
import { getTimeInactivityWarning } from '../../../data/auth/authSelectors'
import { getCsrf } from '../../../data/login/loginActions'
import { setIsLogoutInProgress, setIsSessionTimedOut } from '../../../data/logout/logoutActions'
import { getIsSessionTimedOut } from '../../../data/logout/logoutSelector'
import { trackEventWithObj } from '../../../data/mixpanel'
import mixpanel from '../../../data/mixpanel/mixpanel-browser'
import { TrackEventWithObjAction } from '../../../data/mixpanel/types'
import { getIsUserLoggedIn } from '../../../data/user/userSelectors'
import { broadcastSessionTermination } from '../../hooks/useSessionSync'
import { clearPendoSession } from '../../utils/pendoLoader'
import { updateStoredProviderResultsSurveyValues } from '../../utils/pendoUtils'

type InactivityWarningScreenProps = {
  isUserLoggedIn: boolean
  isSessionTimedOut: boolean
  timeInactivityWarning: number
  setIsSessionTimedOut: (type: boolean) => void
  setTimeInactivityWarning: (value: number | null) => void
  setTimeLastActive: (value: number) => void
  trackEventWithObj: TrackEventWithObjAction
  getCsrf: () => void
  setIsLogoutInProgress: (value: boolean) => void
}

const INACTIVITY_WARNING_ID = 'InactivityWarning'

const InactivityWarningScreen: React.FC<InactivityWarningScreenProps> = ({
  isUserLoggedIn,
  isSessionTimedOut,
  timeInactivityWarning,
  setIsSessionTimedOut,
  setTimeInactivityWarning,
  setTimeLastActive,
  trackEventWithObj,
  getCsrf,
  setIsLogoutInProgress,
}) => {
  const [showModal, setShowModal] = useState<boolean>(isSessionTimedOut || !!timeInactivityWarning)
  const [warningDuration, setWarningDuration] = useState<number>(0)

  const handleModalClose = async () => {
    setShowModal(false)
    portal?.remove()

    if (isUserLoggedIn) {
      trackEventWithObj({
        event: INACTIVITY_EVENTS.RESUME,
      })
      setTimeInactivityWarning(null)
      setTimeLastActive(Date.now())
      await getCsrf() // re-fetch CSRF token since the current one might be stale (30 min limit)
    } else {
      setIsSessionTimedOut(false)
    }
  }

  const handlePressSignOut = () => {
    setIsLogoutInProgress(true)
    trackEventWithObj({
      event: INACTIVITY_EVENTS.SIGN_OUT,
      [COMMON_MIXPANEL_PROPERTIES.TRIGGER]: INACTIVITY_PROPERTIES.USER_SIGN_OUT,
    })
    mixpanel.reset()
    clearPendoSession()
    updateStoredProviderResultsSurveyValues()
    broadcastSessionTermination()
    window.location.href = '/logout'
  }

  const handleWarningTimeUp = () => {
    trackEventWithObj({
      event: INACTIVITY_EVENTS.SIGN_OUT,
      [COMMON_MIXPANEL_PROPERTIES.TRIGGER]: INACTIVITY_PROPERTIES.SESSION_EXPIRED,
    })
    setShowModal(false)
    broadcastSessionTermination(true)
    setWarningDuration(0)
  }

  const portal = document.getElementById(INACTIVITY_WARNING_ID)
  useEffect(() => {
    if (timeInactivityWarning && !showModal) {
      setShowModal(true)
      setWarningDuration(Math.max(0, (INACTIVITY_WARNING_DURATION_MS - (Date.now() - timeInactivityWarning)) / 1000))
      trackEventWithObj({
        event: INACTIVITY_EVENTS.INACTIVE,
      })
    }
  }, [showModal, timeInactivityWarning, trackEventWithObj])

  const createPortal = useCallback(() => {
    // Sets a div at the bottom of the DOM tree for the modal to be rendered to keep the inactivity modal
    // stacked on top of all other Modals or FullScreenOverlays
    if (!portal) {
      const newPortal = document.createElement('div')
      newPortal.id = INACTIVITY_WARNING_ID
      document.body.appendChild(newPortal)
    }
  }, [portal])

  useEffect(() => {
    if (!portal && (isSessionTimedOut || timeInactivityWarning)) {
      createPortal()
    }
  }, [isSessionTimedOut, portal, timeInactivityWarning, showModal, createPortal])

  useEffect(() => {
    const url = new URL(window.location.href)

    // 'expired' query param will be present when the session expires on a 1Lyra page
    if (window.location.pathname === '/login' && url.searchParams.get('expired') === 'true') {
      setIsSessionTimedOut(true)
      setShowModal(true)
      url.searchParams.delete('expired')
      window.history.replaceState({}, '', url.toString())
      createPortal()
    }
  }, [createPortal, setIsSessionTimedOut])

  const portalContent = (
    <InactivityWarningModal
      visible={showModal}
      onRequestClose={handleModalClose}
      warningDuration={warningDuration}
      onPressResume={handleModalClose}
      onPressSignIn={handleModalClose}
      onPressSignOut={handlePressSignOut}
      onWarningTimeUp={handleWarningTimeUp}
      onPressClose={handleModalClose}
      inactivityState={isSessionTimedOut ? INACTIVITY_STATE.SIGNED_OUT : undefined}
    />
  )

  const modal = portal && ReactDOM.createPortal(portalContent, portal)
  return modal
}

const mapStateToProps = (state: any) => {
  return {
    isUserLoggedIn: getIsUserLoggedIn(state),
    isSessionTimedOut: getIsSessionTimedOut(state),
    timeInactivityWarning: getTimeInactivityWarning(state),
  }
}

export default connect(mapStateToProps, {
  setIsSessionTimedOut,
  setTimeInactivityWarning,
  setTimeLastActive,
  trackEventWithObj,
  getCsrf,
  setIsLogoutInProgress,
})(InactivityWarningScreen)
